/**
 * useSectionCompletions — Hook for managing section completion state
 * Fetches completions from API, provides toggle function
 * Sprint 8 — S8-24
 */

import { useState, useEffect, useCallback } from 'react'

export interface SectionCompletionRecord {
  id: number
  project_id: number
  tab_id: string
  section_id: string
  completed_by: number | null
  completed_at: string | null
  completed_by_name: string | null
}

export interface SectionCompletionState {
  completions: SectionCompletionRecord[]
  isLoading: boolean
  isSectionComplete: (tabId: string, sectionId: string) => boolean
  getCompletionInfo: (tabId: string, sectionId: string) => SectionCompletionRecord | undefined
  toggleSection: (tabId: string, sectionId: string) => Promise<void>
  getTabCompletedCount: (tabId: string, totalSections: number) => { completed: number; total: number }
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export function useSectionCompletions(projectId: number): SectionCompletionState {
  const [completions, setCompletions] = useState<SectionCompletionRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Fetch completions
  useEffect(() => {
    const fetchCompletions = async () => {
      try {
        const token = localStorage.getItem('hos_token')
        const res = await fetch(`${API_BASE}/api/v2/projects/${projectId}/section-completions`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (res.ok) {
          const data = await res.json()
          setCompletions(data.completions || [])
        }
      } catch (err) {
        console.error('Failed to fetch section completions:', err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchCompletions()
  }, [projectId])

  const isSectionComplete = useCallback(
    (tabId: string, sectionId: string) =>
      completions.some(c => c.tab_id === tabId && c.section_id === sectionId),
    [completions]
  )

  const getCompletionInfo = useCallback(
    (tabId: string, sectionId: string) =>
      completions.find(c => c.tab_id === tabId && c.section_id === sectionId),
    [completions]
  )

  const toggleSection = useCallback(async (tabId: string, sectionId: string) => {
    try {
      const token = localStorage.getItem('hos_token')
      const res = await fetch(`${API_BASE}/api/v2/projects/${projectId}/section-completions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tab_id: tabId, section_id: sectionId }),
      })

      if (res.ok) {
        const data = await res.json()
        if (data.action === 'completed') {
          // Add to local state (optimistic)
          setCompletions(prev => [
            ...prev,
            {
              id: Date.now(), // temp ID, will be replaced on next fetch
              project_id: projectId,
              tab_id: tabId,
              section_id: sectionId,
              completed_by: null,
              completed_at: new Date().toISOString(),
              completed_by_name: null,
            },
          ])
        } else {
          // Remove from local state
          setCompletions(prev =>
            prev.filter(c => !(c.tab_id === tabId && c.section_id === sectionId))
          )
        }
      }
    } catch (err) {
      console.error('Failed to toggle section completion:', err)
    }
  }, [projectId])

  const getTabCompletedCount = useCallback(
    (tabId: string, totalSections: number) => {
      const completed = completions.filter(c => c.tab_id === tabId).length
      return { completed, total: totalSections }
    },
    [completions]
  )

  return {
    completions,
    isLoading,
    isSectionComplete,
    getCompletionInfo,
    toggleSection,
    getTabCompletedCount,
  }
}
